import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { formatNumber, formatMoney } from '../../utils/format';
import { getCompanyTimeYear } from '../../api/hrTime';
import { getChrYearly } from '../../api/chrYearly';
import { fetchPersonnelCostCascade } from '../../api/tdabc/personnelCostCascade';
import { getHypotheticalIndirect, upsertHypotheticalIndirect } from '../../api/tdabc/hypotheticalIndirect';
import { computeCcr } from './ccrModel';
import StoryDeck from '../../components/StoryDeck';
import './PeopleStory.css';
import './CcrStory.css';

/* Le coût horaire (CCR), exposé chapitre par chapitre — 2026-08-22.

   Même gabarit que la Synthèse People : un cadre à défilement magnétique, un
   chapitre par écran, un rail de navigation. La feuille de style est celle du
   récit People (`PeopleStory.css`) — deux récits, une seule grammaire.

   Ce que le tableau ne pouvait pas dire, et que le récit dit : le CCR n'est pas
   un chiffre, c'est une CHAÎNE. Le calendrier fixe des jours, les jours donnent
   des heures, les heures se partagent entre production et structure, la
   structure redevient du coût, et le coût se répartit sur ce qui reste. Chaque
   chapitre expose une maille de cette chaîne, les chiffres qui l'alimentent, et
   le lien vers l'écran où les modifier.

   Aucun calcul nouveau : tout est lu des moteurs existants (calendrier, chr
   yearly, cascade de coût personnel). Le seul montant saisissable est la base
   de charges indirectes — le seul du récit qui ne se déduit d'aucun autre. */

export default function CcrStory({ activeTenant, year, unit = 'chf', setPage, onGoDetail, onGoTab, part = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  /* Deck, rail, clavier et mémoire du chapitre : StoryDeck. Le chapitre courant
     SURVIT à la sortie, mémorisé par client ET par moitié (LCR / CCR) — chaque
     chapitre porte un lien vers un écran de saisie, et revenir en tête du récit
     obligeait à tout reparcourir. */

  const [cal, setCal] = useState(null);
  // Année précédente : le nombre de jours ouvrés varie d'une année à l'autre
  // (la répartition des fériés sur le week-end ne se répète pas). Sans elle,
  // « 252 jours » ne se situe nulle part.
  const [calPrev, setCalPrev] = useState(null);
  const [chr, setChr] = useState(null);
  const [casc, setCasc] = useState(null);
  const [base, setBase] = useState(null);      // base de charges indirectes (saisie)
  const [baseDraft, setBaseDraft] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || !year) return undefined;
    let alive = true;
    setLoading(true);
    Promise.all([
      getCompanyTimeYear(tenantId, year),
      getCompanyTimeYear(tenantId, year - 1).catch(() => null),
      getChrYearly(tenantId, year),
      fetchPersonnelCostCascade(tenantId, year),
      getHypotheticalIndirect(tenantId, year),
    ]).then(([c, cp, y, k, h]) => {
      if (!alive) return;
      setCal(c && !c.error ? c.data : null);
      setCalPrev(cp && !cp.error ? cp.data : null);
      setChr(y && !y.error ? y.data : null);
      setCasc(k || null);
      const amt = h && !h.error ? Number(h.data?.amount ?? h.data ?? 0) : 0;
      setBase(amt);   // le brouillon suit, dans l'unité de la colonne (effet ci-dessous)
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, year]);

  /* ── Les mailles de la chaîne ──────────────────────────────────────────── */
  const S = useMemo(() => {
    if (!chr || !casc) return null;
    const rows = chr.rows || [];
    const tot = chr.totals || {};
    const ct = casc.totals || {};
    const num = (v) => Number(v) || 0;

    const fte = rows.reduce((a, r) => a + num(r.fte_pct), 0) / 100;
    const to = rows.reduce((a, r) => a + num(r.heures_contractuelles), 0);
    const vac = rows.reduce((a, r) => a + num(r.holidays_hours), 0);
    const tt = to + vac;

    const pools = (casc.pools || [])
      .map((q) => ({
        n: q.pool_name || (q.is_unassigned ? p('Non affecté') : p('Groupe')),
        to: num(q.working_hours), gest: num(q.corporate_hours), sup: num(q.supervision_hours),
        tr: num(q.production_hours), pers: num(q.personnel), corp: num(q.corporate),
        prod: num(q.productive_labour),
      }))
      .filter((q) => q.pers > 0 || q.tr > 0);

    const tr = num(ct.production_hours);
    const gest = pools.reduce((a, q) => a + q.gest, 0);
    const sup = pools.reduce((a, q) => a + q.sup, 0);
    const corp = num(ct.corporate);
    const feesPers = num(ct.expenses);
    const specific = num(ct.specific_indirect);

    const sell = pools.filter((q) => q.tr > 0)
      .map((q) => ({ ...q, lcr: q.prod / q.tr }))
      .sort((a, b) => b.lcr - a.lcr);

    const people = rows
      .filter((r) => num(r.heures_prod) > 0)
      .map((r) => ({
        id: r.person_id,
        n: `${(r.last_name || '').toUpperCase()} ${r.first_name || ''}`.trim(),
        g: r.pool_name || r.department || '—',
        lcr: num(r.lcr), tr: num(r.heures_prod),
      }))
      .sort((a, b) => b.lcr - a.lcr);

    return {
      fte, to, vac, tt, tr, gest, sup, pools, sell, people,
      /* Congés spéciaux : le SOLDE du palier, pas une somme saisie. Le moteur
         pose TR = TO − gestion − supervision − congés spéciaux ; en prenant le
         résidu, la cascade de la slide se recompose toujours exactement. */
      spe: Math.max(0, to - gest - sup - tr),
      gross: num(tot.gross_salary), cs: num(tot.social_charges_empr), loaded: num(tot.loaded_payroll),
      corp, feesPers, specific,
      /* Postes de COÛT de la cascade analytique (à ne pas confondre avec `sup`,
         qui compte des HEURES). Même définition que l'écran Coût par groupe :
         coût complet = travail productif + gestion + supervision + frais de
         personnel + charges indirectes. */
      prodLab: num(ct.productive_labour), supCost: num(ct.supervision),
      indirect: num(ct.indirect_costs),
      /* Route « comptable » du coût complet : on part des charges de personnel
         telles qu'elles sont portées en paie, et on en retire les congés
         spéciaux (Σ LCR × heures). Elle aboutit au centime au même total que
         la route analytique (productif + gestion + supervision) — c'est la
         réconciliation posée par la cascade — mais elle MONTRE l'exclusion,
         que l'autre laissait invisible. */
      personnel: num(ct.personnel), conges: num(ct.absenteism),
    };
  }, [chr, casc]);  // eslint-disable-line

  /* La base de charges indirectes est le SEUL montant saisi du récit, et il
     tombe au milieu d'une colonne qui suit la bascule CHF ⇄ kCHF. Il s'affichait
     en francs bruts (« 500000 ») entre des lignes en milliers (2026-08-26) : à
     la lecture, la base paraissait mille fois trop grosse.
     Le montant EN BASE reste en francs ; seuls l'affichage et la saisie passent
     dans l'unité de la colonne. La division ne s'arrondit pas : basculer
     d'unité ne doit jamais raboter les francs d'un montant déjà saisi. */
  const uFactor = unit === 'kchf' ? 1000 : 1;
  useEffect(() => {
    if (base == null) return;
    setBaseDraft(unit === 'kchf' ? String(base / 1000) : String(Math.round(base)));
  }, [base, unit]);

  const saveBase = () => {
    const v = Math.max(0, Math.round((Number(baseDraft) || 0) * uFactor));
    if (v === Math.round(base || 0)) return;
    setBase(v);
    upsertHypotheticalIndirect(tenantId, year, v).catch(() => {});
  };

  if (!tenantId) return <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>;
  if (loading || !S) return <div className="hr-page__loading">{t('common:status.loading')}</div>;

  const c = cal?.computed || {};
  const n0 = (v) => formatNumber(v, 0);
  const n1 = (v) => formatNumber(v, 1);
  const n2 = (v) => formatNumber(v, 2);
  const mny = (v) => formatMoney(v, unit, 0);
  const chf = unit === 'kchf' ? 'kCHF' : 'CHF';

  const weekly = Number(c.weekly_contractual_hours) || 0;
  const daily = Number(c.daily_contractual_hours) || 0;
  const weekdays = Number(c.weekday_count_year) || 0;
  const official = Number(c.weekday_holidays_year) || 0;
  const offered = Number(c.weekday_offered_year) || 0;
  const openDays = Number(c.working_days_year) || 0;       // jours ouvrés
  const dueDays = openDays - offered;                      // jours dus
  const comp = Number(c.compensated_days_year) || 0;
  const workedDays = Number(c.final_working_days_year) || 0;
  const dueHours = Number(c.t0_annual_hours) || dueDays * daily;
  const hPerDay = workedDays > 0 ? dueHours / workedDays : daily;
  const cPrev = calPrev?.computed || null;
  const prevOpenDays = cPrev ? Number(cPrev.working_days_year) : null;
  const dOpen = prevOpenDays != null ? openDays - prevOpenDays : 0;
  /* Le calendrier se lit en TABLEAU : jours et heures côte à côte, plus
     l'écart à l'exercice précédent. Deux règles :
       · les heures d'un PALIER se calculent depuis ses jours ; celles d'une
         DÉDUCTION depuis la différence entre paliers. Sinon les arrondis se
         décalent (261 × 8.40 = 2'192,4 et 9 × 8.40 = 75,6 : 2'192 − 76 = 2'116
         quand le palier vaut 2'117) ;
       · les jours RATTRAPÉS ne retirent aucune heure. Ils se reportent sur les
         jours restants, dont la durée s'allonge. Le zéro de la colonne heures
         est le fait, pas un trou.                                            */
  const pv = (k) => (cPrev ? Number(cPrev[k]) || 0 : 0);
  const hasPrev = cPrev != null;
  const hWeek = Math.round(weekdays * daily);
  const hOpen = Math.round(openDays * daily);
  const hWork = Math.round(dueHours);
  const prevWork = pv('working_days_year') - pv('weekday_offered_year');
  const calRows = [
    { k: 'week', lb: `${p('Jours de semaine')}`, d: weekdays, h: hWeek,
      dd: weekdays - pv('weekday_count_year') },
    { k: 'hol', lb: p('− Jours fériés en semaine'), cut: true,
      d: -official, h: -(hWeek - hOpen), dd: official - pv('weekday_holidays_year') },
    { k: 'open', lb: p('= Jours ouvrés'),
      d: openDays, h: hOpen, level: true, dd: openDays - pv('working_days_year') },
    { k: 'off', lb: p('− Congés offerts'), cut: true,
      d: -offered, h: -(hOpen - hWork), dd: offered - pv('weekday_offered_year') },
    { k: 'work', lb: p('= Temps de travail'), d: dueDays, h: hWork, level: true,
      dd: dueDays - prevWork },
    { k: 'comp', lb: p('− Jours rattrapés'), cut: true,
      d: -comp, h: 0, hNa: true, dd: comp - pv('compensated_days_year') },
    { k: 'base', lb: p('= Base avant vacances'),
      d: workedDays, h: hWork, level: true, fin: true,
      dd: workedDays - pv('final_working_days_year'),
      dh: hasPrev ? dueHours - pv('t0_annual_hours') : 0 },
  ];
  /* Un écart est FAVORABLE quand il augmente la base : un palier qui monte, une
     déduction qui recule. Le signe seul ne suffit donc pas à colorer. */
  const dSign = (r) => (r.cut ? -r.dd : r.dd);

  /* Les frais SPÉCIFIQUES sont saisis sur un groupe : ils sortent de la base à
     répartir, sinon ils comptent deux fois (une fois sur leur groupe, une fois
     dans le solde mutualisé). Sans effet aujourd'hui, aucun client n'en saisit,
     mais la règle doit être posée avant qu'un le fasse. */
  const gen = S.feesPers + Math.max(0, (Number(base) || 0) - S.specific);
  /* Total et taux viennent de `ccrModel`, partagé avec le récit People : deux
     copies de cette formule donneraient deux CCR qui divergent au premier
     changement de règle, sur deux écrans lus à cinq minutes d'intervalle.
     La cascade se recompose donc toujours, et égale la route analytique
     (productif + gestion + supervision + frais) au centime sur les quatre
     clients — c'est la réconciliation « charges à allouer = coût total du
     personnel ». */
  const { costTotal: costTot, ccr } = computeCcr(casc.totals, base);
  const ccrFull = ccr ?? 0;
  const corpH = S.tr > 0 ? S.corp / S.tr : 0;
  const genH = S.tr > 0 ? gen / S.tr : 0;
  const maj = corpH + genH;
  const avgLcr = S.tr > 0 ? S.loaded / S.to : 0;
  const sellAvg = S.sell.reduce((a, q) => a + q.lcr * q.tr, 0) / (S.sell.reduce((a, q) => a + q.tr, 0) || 1);
  const maxCcr = Math.max(1, ...S.sell.map((q) => q.lcr + maj));

  /* `left` place le segment sur l'axe. Une déduction se dessine LÀ OÙ ELLE
     AMPUTE — collée au solde précédent, du côté droit — et le total suivant
     repart de zéro : on lit une cascade qui descend, pas deux barres qu'on
     comparerait. Sans `left`, le segment part de la gauche (cas des totaux).
     Une ligne de déduction ne peint PAS de piste grise pleine largeur : on ne
     doit y voir que ce qui est retiré, sinon l'œil compare deux longueurs au
     lieu de lire une amputation (retour du 2026-08-22, lignes gestion /
     supervision). */
  const Bar = ({ label, sub, value, pct, left = 0, tone, total }) => (
    <div className={`ccs-bar${total ? ' ccs-bar--total' : ''}`}>
      <span className="ccs-bar__k">{label}{sub && <small>{sub}</small>}</span>
      <span className={`ccs-bar__t${left > 0 ? ' ccs-bar__t--cut' : ''}`}>
        {pct > 0 && (
          <i className={`ccs-bar__f${tone ? ` ccs-bar__f--${tone}` : ''}`}
             style={{ left: `${Math.max(0, Math.min(100, left))}%`, width: `${Math.min(100, pct)}%` }} />
        )}
      </span>
      <span className="ccs-bar__v">{value}</span>
    </div>
  );
  // Part d'une base, en % — la cascade se lit toujours sur la même échelle.
  const pctOf = (v, base) => (base > 0 ? (v / base) * 100 : 0);
  const Src = ({ children }) => <div className="ccs-src">{children}</div>;
  /* RÈGLE DES RENVOIS (2026-08-26) : quand la cible est un ONGLET DU HUB qui
     nous accueille, on y bascule ; on ne quitte la section que si elle n'a pas
     l'onglet. `page`/`tab` portent la navigation externe, `onClick` la bascule
     locale, qui gagne quand elle est fournie.
     Deux liens tombaient dans le vide faute de bascule : « Salaire horaire
     (LCR) », qui appelait `setPage` vers la section où l'on se trouve déjà (donc
     rien ne bougeait), et « Temps standard », qui envoyait dans la section Temps
     alors que les deux hubs montent cet onglet.
     Le LIBELLÉ suit : il ne porte le chemin (« Temps › Calendrier ») que
     lorsqu'on part vraiment ailleurs. Un renvoi local prend le nom de son
     onglet, repris de la table des libellés pour qu'il suive tout renommage. */
  const goSetting = (tabId) => (onGoTab ? () => onGoTab(tabId) : null);
  const Go = ({ page, tab, onClick, label, hint }) => (
    <button type="button" className="ccs-go"
            onClick={() => (onClick ? onClick() : (setPage && setPage(page, tab)))}>
      {label}{hint && <small>{hint}</small>}
    </button>
  );

  /* Les huit chapitres, dans l'ordre de la chaîne. Le récit se SCINDE :
     les trois premiers construisent le salaire horaire (section LCR), les
     cinq suivants le coût horaire (section CCR). Chaque moitié repart à 1 —
     d'où une numérotation explicite plutôt qu'un compteur CSS, qui ne sait
     pas se réinitialiser au milieu d'une liste. */
  const CH = [
    { k: 'cal', lb: p('Le calendrier'), tone: '',
      node: (
        <>
    <h2>
      {/* Le titre compte des JOURS ; l'écart en heures, qui peut aller en sens
          inverse, se lit dans la colonne « vs N-1 » du tableau. */}
      {p('L’année')} {year} {p('compte')} {n0(openDays)} {p('jours ouvrés')}
      {prevOpenDays != null && dOpen !== 0
        ? `, ${p('soit')} ${n0(Math.abs(dOpen))} ${Math.abs(dOpen) > 1 ? p('jours') : p('jour')} ${dOpen > 0 ? p('de plus') : p('de moins')} ${p('qu’en')} ${year - 1}.`
        : '.'}
    </h2>
    <p className="pst-lede">
      {p('À raison de')} <b>{n1(weekly)} {p('h hebdomadaires contractuelles')}</b>{p(', soit')} {n2(daily)} {p('h par jour, la base de travail atteint')} {n0(dueHours)} {p('heures à 100 %.')}
      {' '}{p('Réparties sur')} {n0(workedDays)} {p('jours, elles donnent un temps effectif de')} {n2(hPerDay)} {p('h par jour.')}
    </p>
    <div className="ccs-caltbl">
      <table className="ccs-cal">
        <thead>
          <tr>
            <th>{p('Calendrier')} {year}</th>
            <th>{p('Jours')}</th>
            <th>{p('Heures')}</th>
            {hasPrev && <th>{p('vs')} {year - 1}</th>}
          </tr>
        </thead>
        <tbody>
          {calRows.map((r) => (
            <tr key={r.k} className={r.fin ? 'is-fin' : (r.level ? 'is-lvl' : (r.cut ? 'is-cut' : ''))}>
              {/* UNE ligne de texte par ligne de tableau (2026-08-23) : les
                  gloses doublaient la hauteur, et le tableau ne tenait plus
                  dans l'écran. Ce qu'elles disaient est dans le chapô ou dans
                  les tuiles. */}
              <td>{r.lb}</td>
              <td className={r.d ? '' : 'is-nil'}>{n0(r.d)}</td>
              {/* « n.a. » plutôt que 0 : un rattrapage ne retire pas zéro heure,
                  la colonne ne s'applique pas à lui. Le zéro se lisait comme un
                  montant, donc comme une coïncidence. */}
              <td className={r.hNa || !r.h ? 'is-nil' : ''}>{r.hNa ? p('n.a.') : n0(r.h)}</td>
              {hasPrev && (
                <td className={`ccs-cal__d${dSign(r) > 0 ? ' is-up' : (dSign(r) < 0 ? ' is-dn' : ' is-flat')}`}>
                  {r.dd ? `${r.dd > 0 ? '+' : '−'}${n0(Math.abs(r.dd))} ${p('j')}` : '='}
                  {/* Ligne finale : les jours et les heures peuvent diverger —
                      +1 jour et −40 h le même exercice. On montre les deux. */}
                  {r.dh != null && Math.round(r.dh) !== 0 && (
                    <b className={r.dh > 0 ? 'is-up' : 'is-dn'}>
                      {r.dh > 0 ? '+' : '−'}{n0(Math.abs(r.dh))} {p('h')}
                    </b>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* Les trois chiffres à retenir : ce que l'employeur doit, sur combien de
        jours cela retombe, et la journée qui en résulte. Les heures/semaine ont
        quitté les tuiles — elles ouvrent déjà le chapô, en gras. */}
    <div className="pst-figs pst-figs--tight">
      <div className="pst-fig">
        <div className="pst-fig__v">{n0(dueHours)}<small>h</small></div>
        <div className="pst-fig__k">{p('Heures dues avec vacances')}</div>
      </div>
      <div className="pst-fig">
        <div className="pst-fig__v">{n0(workedDays)}<small>{p('jours')}</small></div>
        <div className="pst-fig__k">{p('Jours travaillés')}</div>
      </div>
      <div className="pst-fig">
        <div className="pst-fig__v">{n2(hPerDay)}<small>h</small></div>
        <div className="pst-fig__k">{p('Temps effectif par jour')}</div>
      </div>
    </div>
    <Src>
      <Go page="hr-time-hub" label={p('Temps › Calendrier')} hint={p('heures/semaine, fériés, congés offerts')} />
    </Src>
        </>
      ) },
    { k: 'jour', lb: p('Les heures par jour'), tone: 'tint',
      node: (
        <>
    <h2>{p('Durant le temps ouvert de l’entreprise, les')} {n1(S.fte)} {p('EPT cumulent')} {n0(S.to)} {p('heures sur l’année.')}</h2>
    <p className="pst-lede">
      {p('La moyenne des EPT tient compte des entrées, des sorties et des changements de taux. Le temps total est annualisé.')}
    </p>
    <div className="ccs-bars">
      <div className="ccs-unit">{p('heures')}</div>
      <Bar label={p('Base d’heures avant vacances, 1 EPT à 100 %')} value={n0(dueHours)} pct={0} />
      <Bar label={p('× Effectif')} value={`${n1(S.fte)} EPT`} pct={0} />
      <Bar label={p('= Temps total (TT)')} value={n0(S.tt)} pct={100} total />
      <Bar label={p('− Vacances')}
           value={`−${n0(S.vac)}`} left={pctOf(S.to, S.tt)} pct={pctOf(S.vac, S.tt)} tone="soft" />
      <Bar label={p('= Temps ouvert (TO)')} value={n0(S.to)} pct={pctOf(S.to, S.tt)} total />
    </div>
    <div className="pst-figs pst-figs--tight">
      <div className="pst-fig">
        <div className="pst-fig__v">{n1(workedDays)}<small>{p('jours')}</small></div>
        <div className="pst-fig__k">{p('Jours travaillés')}</div>
      </div>
      <div className="pst-fig">
        <div className="pst-fig__v">{n2(hPerDay)}<small>{p('h/jour')}</small></div>
        <div className="pst-fig__k">{p('Heures dues par jour')}</div>
      </div>
      <div className="pst-fig">
        <div className="pst-fig__v">{n0(S.to)}<small>h</small></div>
        <div className="pst-fig__k">{p('Temps ouvert de l’effectif')}</div>
      </div>
    </div>
    <Src>
      <Go page="hr-time-hub" label={p('Temps › Calendrier')} hint={p('jours rattrapés')} />
      <Go page="time-standard" onClick={goSetting('standard')}
          label={t('people:ccr.tabs.standard')} hint={p('le détail par personne')} />
    </Src>
        </>
      ) },
    { k: 'lcr', lb: p('Le salaire horaire'), tone: '',
      node: (
        <>
    <h2>{p('Le salaire horaire chargé moyen s’établit à')} {n2(avgLcr)} {p('CHF.')}</h2>
    <p className="pst-lede">
      {p('Ce salaire horaire inclut les éléments soumis à charges sociales. Il ne tient compte ni des frais de personnel, ni des indemnités et contributions.')}
    </p>
    <div className="ccs-bars">
      <div className="ccs-unit">{chf}</div>
      <Bar label={p('Rémunération')} sub={p('base et compléments')} value={mny(S.gross)}
           pct={pctOf(S.gross, S.loaded)} />
      <Bar label={p('+ Charges sociales employeur')}
           sub={S.gross ? `${n1((S.cs / S.gross) * 100)} % ${p('— LPP comprise')}` : null}
           value={mny(S.cs)} left={pctOf(S.gross, S.loaded)} pct={pctOf(S.cs, S.loaded)} tone="soft" />
      <Bar label={p('= Masse salariale chargée')} value={mny(S.loaded)} pct={100} total />
    </div>
    <div className="pst-figs pst-figs--tight">
      <div className="pst-fig"><div className="pst-fig__v">{formatMoney(S.loaded, 'kchf', 0)}<small>kCHF</small></div>
        <div className="pst-fig__k">{p('Masse salariale chargée')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n0(S.to)}<small>h</small></div>
        <div className="pst-fig__k">{p('Temps ouvert')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n2(avgLcr)}<small>CHF/h</small></div>
        <div className="pst-fig__k">{p('Salaire horaire (LCR)')}</div></div>
    </div>
    <Src><Go page="hr-lcr-hub" onClick={onGoDetail}
             label={p('Salaire horaire (LCR)')} hint={p('le détail par personne')} /></Src>
        </>
      ) },
    /* Ouverture du récit CCR : la thèse d'abord. On annonce le coût complet et
       le taux qui en sort, les chapitres suivants construisent chaque couche.
       (Ajouté le 2026-08-23 : le récit démarrait sur la capacité pratique, donc
       sur un dénominateur, sans avoir dit à quoi il servait.) */
    { k: 'complet', lb: p('Le coût complet'), tone: 'tint',
      node: (
        <>
    <h2>{p('Le coût complet d’une heure disponible s’établit en moyenne à')} {n2(ccrFull)} {p('CHF.')}</h2>
    <p className="pst-lede">
      {p('Pour obtenir un coût complet, il convient d’ajouter aux salaires directs : le personnel indirect, les frais spécifiques du groupe et les frais généraux. Les congés spéciaux (indemnisés ou non rémunérés) sont retirés de la capacité disponible et des coûts. Le taux horaire obtenu valorise ainsi une heure disponible et permet ')}
      <b>{p('la valorisation d’une activité')}</b>
      {p(' (selon le concept d’Activity Based Costing), que ce soit la réalisation d’un service, d’un projet, d’une formation ou toute heure non facturée.')}
    </p>
    <div className="ccs-bars">
      {/* L'unité se dit UNE fois, en tête : la répéter sur cinq lignes de
          montants alourdit sans rien apprendre. Elle suit la bascule. */}
      <div className="ccs-unit">{chf}</div>
      {/* Une seule marche DESCEND, et c'est tout l'intérêt : posée entre la
          masse salariale et les frais, l'exclusion des congés spéciaux se voit
          au lieu de se déduire. */}
      {/* Sans sous-titres : cinq lignes de glose doublaient la hauteur du
          graphique pour redire ce que le chapô vient d'expliquer. */}
      <Bar label={p('Charges de personnel')}
           value={mny(S.personnel)} pct={pctOf(S.personnel, costTot)} />
      <Bar label={p('− Congés spéciaux')}
           value={S.conges ? `−${mny(S.conges)}` : '0'}
           left={pctOf(S.personnel - S.conges, costTot)} pct={pctOf(S.conges, costTot)} tone="soft" />
      <Bar label={p('＋ Frais spécifiques')}
           value={mny(S.specific)}
           left={pctOf(S.personnel - S.conges, costTot)} pct={pctOf(S.specific, costTot)} tone="amber" />
      <Bar label={p('＋ Frais généraux')}
           value={mny(gen)}
           left={pctOf(S.personnel - S.conges + S.specific, costTot)} pct={pctOf(gen, costTot)} tone="amber" />
      <Bar label={p('= Coût complet')} value={mny(costTot)} pct={100} total />
    </div>
    <div className="pst-figs pst-figs--tight">
      <div className="pst-fig"><div className="pst-fig__v">{mny(costTot)}<small>{chf}</small></div>
        <div className="pst-fig__k">{p('Coût complet')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n0(S.tr)}<small>h</small></div>
        <div className="pst-fig__k">{p('Capacité pratique')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n2(ccrFull)}<small>CHF/h</small></div>
        <div className="pst-fig__k">{p('Coût horaire (CCR)')}</div></div>
    </div>
        </>
      ) },
    { k: 'tr', lb: p('La capacité pratique'), tone: '',
      node: (
        <>
    {/* Le titre disait « retient X heures sur Y » : deux nombres bruts que le
        lecteur devait rapporter l'un à l'autre lui-même, alors que c'est
        justement la PART qui se commente (le repère Kaplan étant 80–85 %). */}
    <h2>
      {p('La capacité pratique (TR) est de')} {n0(S.tr)} {p('heures, soit')}{' '}
      {n0(pctOf(S.tr, S.to))} {p('% du temps ouvert (TO).')}
    </h2>
    <p className="pst-lede">
      {p('Pour déterminer la profitabilité d’un service, il est nécessaire d’avoir un ')}
      <b>{p('coût complet')}</b>
      {p(' des ressources engagées dans la « production » de ce service. Pour ne pas surévaluer le coût d’un service, nous recherchons un temps « normal » ou « standard », qui soit pérenne et comparable sur plusieurs années.')}
    </p>
    <p className="pst-lede">
      {p('Ce coût complet « normal » comprend les charges du personnel productif ainsi que :')}
    </p>
    <ul className="pst-lede pst-list">
      <li>{p('le temps de supervision réparti au sein du groupe concerné')}</li>
      <li>{p('le temps de gestion réparti de manière transversale selon les heures productives')}</li>
    </ul>
    <div className="ccs-bars">
      <div className="ccs-unit">{p('heures')}</div>
      <Bar label={p('Temps ouvert (TO)')} sub={p('temps travaillé normal')} value={n0(S.to)} pct={100} />
      {/* Le sous-titre DÉFINIT la catégorie, il n'inventorie pas les groupes.
          Listés en direct, ils faisaient apparaître un groupe de production
          qui porte quelques heures de gestion (Ingénieurs, 1'027 h sur
          GeoConseils 2025) au milieu des fonctions de structure : le lecteur
          y lisait une définition, et elle était fausse. Les heures, elles,
          restent toutes dans la barre. */}
      <Bar label={p('− Gestion entreprise')}
           sub={p('direction, administration, ventes')}
           value={S.gest ? `−${n0(S.gest)}` : '0'}
           left={pctOf(S.to - S.gest, S.to)} pct={pctOf(S.gest, S.to)} tone="soft" />
      <Bar label={p('− Supervision équipes opérationnelles')}
           value={S.sup ? `−${n0(S.sup)}` : '0'}
           left={pctOf(S.to - S.gest - S.sup, S.to)} pct={pctOf(S.sup, S.to)} tone="soft" />
      <Bar label={p('− Congés spéciaux')} sub={p('réduction de capacité planifiée')}
           value={S.spe ? `−${n0(S.spe)}` : '0'}
           left={pctOf(S.tr, S.to)} pct={pctOf(S.spe, S.to)} tone="soft" />
      <Bar label={p('= Capacité pratique (TR)')}
           sub={p('temps requis en production')}
           value={n0(S.tr)} pct={pctOf(S.tr, S.to)} total />
    </div>
    <Src>
      <Go page="time-standard" onClick={goSetting('standard')}
          label={t('people:ccr.tabs.standard')} hint={p('les heures de gestion et de supervision')} />
      <Go page="cost-rates-hub" tab="settings" onClick={goSetting('settings')}
          label={p('Groupes ressources')} hint={p('qui produit, qui gère')} />
    </Src>
        </>
      ) },
    { k: 'gest', lb: p('Le coût de la gestion'), tone: 'deep',
      node: (
        <>
    <h2>{p('Le temps de gestion représente')} {mny(S.corp)} {chf} {p('de charge à répartir.')}</h2>
    <p className="pst-lede">
      {p('Ces heures correspondent aux fonctions d’administration, de vente et de direction. Leur coût est réparti sur la capacité pratique : chaque heure disponible en supporte une part, au prorata des heures.')}
    </p>
    <div className="ccs-bars">
      <div className="ccs-unit">{chf}</div>
      {/* Les groupes s'EMPILENT : chacun se pose là où le précédent s'arrête,
          et l'ensemble compose le personnel indirect. */}
      {(() => {
        let acc = 0;
        return S.pools.filter((q) => q.corp > 0).sort((a, b) => b.corp - a.corp).map((q) => {
          const left = pctOf(acc, S.corp);
          acc += q.corp;
          return (
            <Bar key={q.n} label={q.n}
                 sub={q.tr > 0 ? `${p('productif à moitié ·')} ${n0(q.tr)} ${p('h disponibles')}` : p('aucune heure disponible')}
                 value={mny(q.corp)} left={left} pct={pctOf(q.corp, S.corp)} />
          );
        });
      })()}
      <Bar label={p('= Personnel indirect')} value={mny(S.corp)} pct={100} total />
    </div>
    <div className="pst-figs pst-figs--tight">
      <div className="pst-fig"><div className="pst-fig__v">{mny(S.corp)}<small>{chf}</small></div>
        <div className="pst-fig__k">{p('Personnel indirect')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n0(S.tr)}<small>h</small></div>
        <div className="pst-fig__k">{p('Capacité pratique')}</div></div>
      <div className="pst-fig"><div className="pst-fig__v">{n2(corpH)}<small>CHF/h</small></div>
        <div className="pst-fig__k">{p('Majoration')}</div></div>
    </div>
        </>
      ) },
    { k: 'frais', lb: p('Les frais'), tone: 'tint',
      node: (
        <>
    <h2>{p('Les frais suivent deux règles d’affectation distinctes.')}</h2>
    <p className="pst-lede">
      {p('Les frais spécifiques sont imputés au groupe qui les engage. Les frais généraux — loyer, informatique, assurances — ne se rattachent à aucun groupe et sont répartis sur la capacité pratique, selon la même clé que le personnel indirect.')}
    </p>
    <div className="ccs-bars">
      <div className="ccs-unit">{chf}</div>
      <Bar label={p('Frais spécifiques')} sub={p('affectation directe au groupe')}
           value={mny(S.specific)} pct={0} tone="amber" />
      <Bar label={p('Frais de personnel mutualisés')} sub={p('forfaits, indemnités, autres')}
           value={mny(S.feesPers)} pct={pctOf(S.feesPers, gen)} tone="amber" />
      <div className="ccs-bar">
        <span className="ccs-bar__k">{p('Base de charges indirectes')}<small>{p('chiffre annuel décidé')}</small></span>
        <span className="ccs-bar__t">
          <i className="ccs-bar__f ccs-bar__f--amber"
             style={{ width: `${gen ? Math.min(100, ((Number(base) || 0) / gen) * 100) : 0}%` }} />
        </span>
        <span className="ccs-bar__v">
          <input type="number" step={unit === 'kchf' ? 10 : 10000} min="0" value={baseDraft}
                 onChange={(e) => setBaseDraft(e.target.value)}
                 onBlur={saveBase}
                 onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
        </span>
      </div>
      <Bar label={p('= Frais généraux à répartir')} value={mny(gen)} pct={100} tone="amber" total />
    </div>
    <p className="pst-foot">
      {p('La base de charges indirectes se saisit directement ici. C’est le seul montant du récit qui ne se déduit d’aucun autre : il relève d’une décision, et sa modification recalcule les deux chapitres suivants.')}
    </p>
    <Src><Go page="hr-social-hub" onClick={goSetting('frais')}
             label={t('people:ccr.tabs.frais')} hint={p('spécifiques par groupe')} /></Src>
        </>
      ) },
    { k: 'groupe', lb: p('Le coût par groupe'), tone: '',
      node: (
        <>
    <h2>{p('La majoration s’élève à')} {n2(maj)} {p('CHF par heure disponible.')}</h2>
    <p className="pst-lede">
      {p('Personnel indirect et frais généraux sont répartis sur la même clé, la capacité pratique. La majoration est donc identique pour tous les groupes : l’écart de coût horaire entre groupes provient exclusivement du salaire, ce qui rend la répartition vérifiable ligne à ligne.')}
    </p>
    <div className="ccs-stacks">
      {[{ n: p('Moyenne'), lcr: sellAvg, avg: true }].concat(S.sell).map((q) => (
        <div className={`ccs-stack${q.avg ? ' ccs-stack--avg' : ''}`} key={q.n}>
          <span className="ccs-stack__n">{q.n}</span>
          <span className="ccs-stack__b">
            <i className="s1" style={{ width: `${(q.lcr / maxCcr) * 100}%` }}>{n1(q.lcr)}</i>
            <i className="s2" style={{ width: `${(corpH / maxCcr) * 100}%` }}>{n1(corpH)}</i>
            <i className="s3" style={{ width: `${(genH / maxCcr) * 100}%` }}>{n1(genH)}</i>
          </span>
          <span className="ccs-stack__v">{n1(q.lcr + maj)}</span>
        </div>
      ))}
    </div>
    <div className="ccs-lgd">
      <span><i className="s1" />{p('Salaire horaire (LCR)')}</span>
      <span><i className="s2" />{p('+ personnel indirect')} {n2(corpH)}</span>
      <span><i className="s3" />{p('+ frais généraux')} {n2(genH)}</span>
    </div>
        </>
      ) },
    { k: 'perso', lb: p('Par personne'), tone: 'deep',
      node: (
        <>
    <h2>
      {p('Le coût horaire par personne s’échelonne de')} {n0(Math.min(...S.people.map((r) => r.lcr)) + maj)}
      {' '}{p('à')} {n0(Math.max(...S.people.map((r) => r.lcr)) + maj)} {p('CHF.')}
    </h2>
    <p className="pst-lede">
      {p('Le même calcul au niveau individuel : le salaire horaire de la personne, augmenté des deux majorations communes. L’écart entre personnes tient au salaire et au taux d’activité, non à la répartition des charges.')}
    </p>
    <table className="ccs-pp">
      <thead>
        <tr>
          <th>{p('Personne')}</th><th>{p('Groupe')}</th>
          <th>{p('LCR')}</th><th>{p('+ ind.')}</th><th>{p('+ frais')}</th><th>{p('CCR')}</th>
        </tr>
      </thead>
      <tbody>
        {S.people.slice(0, 4).map((r) => (
          <tr key={r.id}>
            <td>{r.n}</td><td>{r.g}</td><td>{n2(r.lcr)}</td>
            <td>{n2(corpH)}</td><td>{n2(genH)}</td>
            <td className="ccs-pp__ccr">{n2(r.lcr + maj)}</td>
          </tr>
        ))}
        {S.people.length > 7 && (
          <tr><td colSpan={6} className="ccs-pp__gap">⋯ {S.people.length - 7} {p('autres')} ⋯</td></tr>
        )}
        {S.people.slice(-3).map((r) => (
          <tr key={`b${r.id}`}>
            <td>{r.n}</td><td>{r.g}</td><td>{n2(r.lcr)}</td>
            <td>{n2(corpH)}</td><td>{n2(genH)}</td>
            <td className="ccs-pp__ccr">{n2(r.lcr + maj)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <Src>
      <Go onClick={onGoDetail} label={p('Coût horaire par personne')} hint={p('la table complète')} />
      <Go page="hr-lcr-hub" label={p('Salaire horaire (LCR)')} hint={p('remonter à la source')} />
    </Src>
        </>
      ) },
  ];

  /* `part` : 'lcr' = les trois premiers chapitres, 'ccr' = les cinq suivants.
     Sans lui, le récit entier — utile pour une relecture d'un seul tenant. */
  const shown = part === 'lcr' ? CH.slice(0, 3) : (part === 'ccr' ? CH.slice(3) : CH);

  return (
    <StoryDeck
      numbered
      chapters={shown.map((c) => ({ key: c.k, label: c.lb, tone: c.tone, node: c.node }))}
      storageKey={`ccr_story_ch_${tenantId || ''}_${part || 'all'}`}
    />
  );
}
